// The entry to the controls of the interface: buttons (base.tsx), choice controls (controls.tsx), menus and selects (menus.tsx),
// and here — text fields, chips, avatar, caption of a field, dialog and toasts.

import * as DG from '@radix-ui/react-dialog';
import { createContext, forwardRef, useCallback, useContext, useState, useSyncExternalStore, type InputHTMLAttributes, type ReactNode } from 'react';
import { Icon } from './icons.tsx';
import { Button, cx } from './base.tsx';
import s from './ui.module.css';

export * from './base.tsx';
export * from './controls.tsx';
export * from './menus.tsx';
export * from './Tabs.tsx';
export { PickList, type PickOption } from './PickList.tsx';

/**
 * A line cut with «…» shows its whole text under the pointer (R-U17): the hint is set only when the text does not fit, so a name that
 * fits gets no tooltip. Put on the element that carries the ellipsis.
 */
export function titleIfCut(e: React.MouseEvent<HTMLElement>) {
  const el = e.currentTarget;
  if (el.scrollWidth > el.clientWidth + 1) el.title = el.textContent ?? '';
  else el.removeAttribute('title');
}

/**
 * Узкий экран (телефон): каркас переходит на телефонную вёрстку — выдвижное меню,
 * строка тулбара в две строки. Подписка на ширину окна, а не на медиазапрос в CSS,
 * потому что часть вёрстки меняет порядок элементов.
 */
export function useNarrow(maxWidth = 760): boolean {
  const query = `(max-width: ${maxWidth}px)`;
  return useSyncExternalStore(
    (cb) => {
      const mq = window.matchMedia(query);
      mq.addEventListener('change', cb);
      return () => mq.removeEventListener('change', cb);
    },
    () => window.matchMedia(query).matches,
  );
}

type InputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> & { bare?: boolean; size?: 'sm' | 'md' | 'lg'; invalid?: boolean };
/** The one text field: three heights, `invalid` paints the frame red and tells the screen reader; `bare` — text on the page (titles). */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input({ bare, size = 'md', invalid, className, ...rest }, ref) {
  return <input ref={ref} aria-invalid={invalid || undefined} {...rest} className={cx(s.input, bare && s.inputBare, size !== 'md' && s[`input_${size}`], className)} />;
});

export const TextArea = forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean; bare?: boolean }>(function TextArea({ invalid, bare, className, ...rest }, ref) {
  return <textarea ref={ref} aria-invalid={invalid || undefined} {...rest} className={cx(s.input, bare && s.inputBare, className)} />;
});

const colorVars = (color: string) => ({ '--chip-bg': `var(--c-${color}-bg)`, '--chip-fg': `var(--c-${color}-fg)`, '--chip-dot': `var(--c-${color}-dot)` }) as React.CSSProperties;

export function Chip({ color = 'gray', dot, children, title, onClick, className }: { color?: string; dot?: boolean; children: ReactNode; title?: string; onClick?: () => void; className?: string }) {
  return (
    <span className={cx(s.chip, className)} style={colorVars(color)} title={title} onClick={onClick}>
      {dot && <i className={s.dot} />}
      <span>{children}</span>
    </span>
  );
}

export function RecordChip({ children, onClick, icon }: { children: ReactNode; onClick?: (e: React.MouseEvent) => void; icon?: string }) {
  return (
    <span className={cx(s.chip, s.recordChip)} onClick={onClick} role={onClick ? 'link' : undefined}>
      {icon && <Icon name={icon} size={12} />}
      <span>{children}</span>
    </span>
  );
}

const initials = (name: string) => name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]).join('').toUpperCase();
/** A person's circle: their picture when `src` is given (`avatarUrl(user)`), their initials on their colour otherwise or while it fails. */
export function Avatar({ name, color = 'gray', size = 20, src }: { name: string; color?: string; size?: number; src?: string }) {
  const [broken, setBroken] = useState<string | null>(null);
  const pic = src && broken !== src;
  return (
    <span className={s.avatar} title={name} style={{ ...colorVars(color), width: size, height: size, fontSize: Math.round(size * 0.42) }}>
      {pic ? <img className={s.avatarPic} src={src} alt="" loading="lazy" onError={() => setBroken(src)} /> : initials(name)}
    </span>
  );
}

export function Dialog({ open, onOpenChange, children, label }: { open: boolean; onOpenChange: (o: boolean) => void; children: ReactNode; label: string }) {
  return (
    <DG.Root open={open} onOpenChange={onOpenChange}>
      <DG.Portal>
        <DG.Overlay className={s.overlay} />
        <DG.Content className={s.dialog} aria-describedby={undefined}>
          <DG.Title className="sr-only">{label}</DG.Title>
          {children}
        </DG.Content>
      </DG.Portal>
    </DG.Root>
  );
}

/**
 * A question before an action that is hard to undo (DESIGN.md «Настройки», опасная зона): what follows, «Отмена» and the action,
 * instead of the browser's `confirm()`. `busy` keeps the dialog while the action runs; the caller closes it when done.
 */
export function ConfirmDialog({ open, title, children, action, danger, busy, onConfirm, onClose }: {
  open: boolean; title: string; children: ReactNode; action: string; danger?: boolean; busy?: boolean; onConfirm: () => void; onClose: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }} label={title}>
      <div className={s.confirm}>
        <h2 className={s.confirmTitle}>{title}</h2>
        <div className={s.confirmText}>{children}</div>
        <div className={s.confirmFoot}>
          <Button onClick={onClose}>Отмена</Button>
          <Button variant={danger ? 'danger' : 'primary'} disabled={busy} onClick={onConfirm}>{action}</Button>
        </div>
      </div>
    </Dialog>
  );
}

/**
 * A caption over a control (DESIGN.md «Контролы»): one look for every form of the admin — dialogs, login, block settings — instead
 * of a caption style per screen. `hint` explains, `error` says what is wrong; both sit under the control. `inline` puts the caption
 * on the left (settings panes with many short fields). `inline="end"` — a row of settings (DESIGN.md «Настройки»): the name and
 * its explanation on the left, the control pressed to the right; on a narrow screen the control goes under the text, a switch
 * stays on the right.
 */
export function Field({ label, hint, error, as = 'label', inline, className, children, ...rest }: { label: ReactNode; hint?: ReactNode; error?: ReactNode; as?: 'label' | 'div'; inline?: boolean | 'end'; className?: string; children: ReactNode } & React.HTMLAttributes<HTMLElement>) {
  const Tag = as;
  return (
    <Tag className={cx(s.field, inline === 'end' ? s.fieldEnd : inline && s.fieldInline, className)} {...rest}>
      <span className={s.fieldLabel}>{label}</span>
      {children}
      {hint && <span className={s.fieldHint}>{hint}</span>}
      {error && <span className={s.fieldError} role="alert">{error}</span>}
    </Tag>
  );
}

// --- Toasts --------------------------------------------------------------------------------------

interface Toast { id: number; text: string; kind: 'info' | 'error'; action?: { label: string; run: () => void }; out?: boolean }
/** How long a toast takes to leave (the `--dur-fast` token): it fades out instead of vanishing mid-read. */
const TOAST_OUT_MS = 120;
const ToastCtx = createContext<(t: Omit<Toast, 'id'>) => void>(() => {});
export const useToast = () => useContext(ToastCtx);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<Toast[]>([]);
  const dismiss = useCallback((id: number) => {
    setItems((xs) => xs.map((x) => (x.id === id ? { ...x, out: true } : x)));
    setTimeout(() => setItems((xs) => xs.filter((x) => x.id !== id)), TOAST_OUT_MS);
  }, []);
  const push = useCallback((t: Omit<Toast, 'id'>) => {
    const id = Date.now() + Math.random();
    setItems((xs) => [...xs.slice(-2), { ...t, id }]);
    setTimeout(() => dismiss(id), t.kind === 'error' ? 6000 : 4000);
  }, [dismiss]);
  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div className={s.toastStack} role="status" aria-live="polite">
        {items.map((t) => (
          <div key={t.id} className={cx(s.toast, t.kind === 'error' && s.toastError, t.out && s.toastOut)}>
            <span className={s.itemText}>{t.text}</span>
            {t.action && <Button size="sm" onClick={() => { t.action!.run(); dismiss(t.id); }}>{t.action.label}</Button>}
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

export const ui = s;
